// TestimonialCard — shared editorial card used on /reviews and /business.
//
// Phase 11 — second-pass redesign (supersedes Phase 9 + Phase 10 photo
// treatment). Three structural changes from Phase 10:
//
//   1. Four-line content layout (was three):
//      ─ Quote                (primary, the main thing)
//      ─ Attribution          (e.g. "Anastasia M.")
//      ─ Session label        (sessionType, small caps, brass divider)
//      ─ Context line          (NEW Phase 11 — optional `context` field,
//                              small muted type, hidden when empty)
//
//   2. Featured-card photo treatment: photo is now a SUBTLE atmospheric
//      backdrop, not a foreground feature. Specifically:
//      ─ Photo is rendered with strong porcelain overlay (~80% white)
//        + 2px blur. Photo presence is felt, not seen.
//      ─ Quote text stays DARK (same color as non-featured cards), so
//        the rule "quote always wins" is enforced visually.
//      ─ Single layout everywhere (no mobile-vs-desktop branching for
//        backdrop variant — the faded treatment works identically at
//        every viewport).
//
//   3. Featured gating unchanged from Phase 10: backdrop variant requires
//      BOTH `featured === true` AND a populated `backdropImage`. Eli
//      controls rhythm via the `featured` flag in Studio.
//
// Non-featured cards: clean porcelain bg, no image. Same 4-line layout.

import React from 'react';
import {cn} from '@/lib/utils';
import {Separator} from '@/components/ui/separator';

const Attribution = ({name, type, context}) => (
    <div className="mt-5 sm:mt-6">
        <Separator className="mb-4 bg-[hsl(var(--lux-brass))]/45 w-10" />
        <div className="text-[11px] tracking-[0.18em] uppercase text-foreground/70 font-medium">
            {name}
        </div>
        {type ? (
            <div className="mt-1.5 text-[10px] tracking-[0.18em] uppercase text-muted-foreground">
                {type}
            </div>
        ) : null}
        {context ? (
            <div className="mt-1.5 text-[12px] leading-[1.5] text-muted-foreground/85 font-normal normal-case">
                {context}
            </div>
        ) : null}
    </div>
);

export const TestimonialCard = ({testimonial, testId}) => {
    if (!testimonial) return null;
    const {id, quote, name, type, context, featured, backdropImage, backdropAlt} = testimonial;
    // Phase 10/11 — backdrop variant requires BOTH `featured === true` AND
    // a populated backdropImage. Explicit per-testimonial control via the
    // `featured` flag — no FE algorithmic guesswork on rhythm.
    const hasBackdrop = !!featured && !!backdropImage;

    // ─── Featured: faded-backdrop variant (Phase 11) ─────────────────────
    //
    // Photo is rendered subtly under a near-opaque porcelain overlay +
    // slight blur. Text stays dark, quote remains the dominant visual.
    // This intentionally reverses Phase 9's "photo dominates / white text
    // overlay" approach which the user found too marketing-y.
    if (hasBackdrop) {
        return (
            <article
                data-testid={testId || `testimonial-${id}`}
                data-has-backdrop="true"
                data-featured="true"
                className="relative overflow-hidden rounded-[2px] border border-border flex flex-col h-full bg-[hsl(var(--lux-porcelain))]"
            >
                {/* Photo layer — blurred, low contrast, hidden under overlay */}
                <img
                    src={backdropImage}
                    alt={backdropAlt || ''}
                    loading="lazy"
                    decoding="async"
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                    style={{
                        filter: 'blur(2px) saturate(0.9)',
                        opacity: 0.55,
                    }}
                />
                {/* Porcelain overlay — pulls the photo to the background */}
                <div
                    aria-hidden
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        // Porcelain at high opacity. Subtle gradient gives
                        // the bottom-left corner slightly more photo
                        // presence so the card doesn't feel pasted on flat
                        // canvas.
                        background:
                            'linear-gradient(135deg, hsla(var(--lux-porcelain) / 0.92) 0%, hsla(var(--lux-porcelain) / 0.82) 60%, hsla(var(--lux-porcelain) / 0.78) 100%)',
                    }}
                />
                {/* Content layer */}
                <div className="relative p-7 sm:p-8 lg:p-10 flex flex-col flex-1">
                    <span
                        aria-hidden
                        className="font-serif-display text-5xl leading-none text-[hsl(var(--lux-brass))]/40 select-none mb-2"
                    >
                        &ldquo;
                    </span>
                    <blockquote className="font-serif-display text-[18px] sm:text-[19px] leading-[1.55] text-foreground/90 flex-1">
                        {quote}
                    </blockquote>
                    <Attribution name={name} type={type} context={context} />
                </div>
            </article>
        );
    }

    // ─── Non-featured: clean quote card ──────────────────────────────────
    return (
        <article
            data-testid={testId || `testimonial-${id}`}
            data-has-backdrop="false"
            data-featured={featured ? "true" : "false"}
            className={cn(
                'relative overflow-hidden rounded-[2px]',
                'bg-[hsl(var(--lux-porcelain))] border border-border',
                'p-7 sm:p-8 lg:p-10',
                'flex flex-col h-full',
            )}
        >
            <span
                aria-hidden
                className="font-serif-display text-5xl leading-none text-[hsl(var(--lux-brass))]/40 select-none mb-2"
            >
                &ldquo;
            </span>
            <blockquote className="font-serif-display text-[18px] sm:text-[19px] leading-[1.55] text-foreground/90 flex-1">
                {quote}
            </blockquote>
            <Attribution name={name} type={type} context={context} />
        </article>
    );
};

export default TestimonialCard;
